"use client";

import { ReactNode } from "react";
import Link from "next/link";
import { ArrowLeft, type LucideIcon } from "lucide-react";
import { Button } from "@/components/shadcn/button";
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/shadcn/sidebar";
import { cn } from "@/lib/utils";
import {
  EntryIcon,
  EntryLabel,
  SideNavExpandableEntry,
  SideNavSubEntry,
} from "./SideNavEntry";
import { SideNavShell } from "./SideNavShell";
import { SIDENAV_ICON_BUTTON_CLASS } from "./sidenavShared";

const BackTitle = ({
  title,
  onBack,
  isBackLoading,
  testIdPrefix,
}: {
  title: ReactNode;
  onBack: () => void;
  isBackLoading?: boolean;
  testIdPrefix: string;
}) => {
  const { state } = useSidebar();
  return (
    <div className="flex items-center gap-2 overflow-hidden">
      <Button
        variant="ghost"
        size="icon"
        // The same size as the pin button across the header; on the rail
        // it goes, like the title beside it.
        className={cn(
          SIDENAV_ICON_BUTTON_CLASS,
          "group-data-[collapsible=icon]:hidden"
        )}
        onClick={onBack}
        loading={isBackLoading}
        aria-label="Go back"
        data-testid={`${testIdPrefix}-back`}
      >
        <ArrowLeft className="size-4" strokeWidth={2} />
      </Button>
      {state !== "collapsed" && (
        // A level below the page's own title, which is its h1.
        <h2 className="text-sm font-medium text-sidebar-foreground truncate">
          {title}
        </h2>
      )}
    </div>
  );
};

type ISectionNavTarget = { href: string } | { onSelect: () => void };

export type ISectionNavPage = {
  id: string;
  title: string;
  isActive: boolean;
} & ISectionNavTarget;

export type ISectionNavItem =
  | ({
      id: string;
      title: string;
      icon: ReactNode | LucideIcon;
      isActive: boolean;
      isDisabled?: boolean;
    } & ISectionNavTarget)
  | {
      id: string;
      title: string;
      icon: ReactNode | LucideIcon;
      children: ISectionNavPage[];
    };

export type ISectionNavGroup = { label?: string; items: ISectionNavItem[] };

// A tab list is one stop in the Tab order; the arrow keys, Home and End move
// between its tabs and select the one they land on.
const moveBetweenTabs = (event: React.KeyboardEvent<HTMLUListElement>) => {
  const step = { ArrowDown: 1, ArrowUp: -1 }[event.key];
  if (step === undefined && event.key !== "Home" && event.key !== "End") return;
  const tabs = Array.from(
    event.currentTarget.querySelectorAll<HTMLButtonElement>(
      '[role="tab"]:not([disabled])'
    )
  );
  const at = tabs.findIndex((tab) => tab === document.activeElement);
  const next =
    event.key === "Home"
      ? 0
      : event.key === "End"
        ? tabs.length - 1
        : (at + (step ?? 0) + tabs.length) % tabs.length;
  event.preventDefault();
  tabs[next]?.focus();
  tabs[next]?.click();
};

const SectionNavList = ({ items }: { items: ISectionNavItem[] }) => {
  // Sections that switch in place (a tool's tabs) are tabs to assistive tech.
  const isTabList = items.every(
    (item) => !("children" in item) && "onSelect" in item
  );
  return (
    <SidebarMenu
      className="list-none gap-0.5"
      {...(isTabList && {
        role: "tablist",
        "aria-orientation": "vertical",
        onKeyDown: moveBetweenTabs,
      })}
    >
      {items.map((item) => (
        <SectionNavEntry key={item.id} item={item} />
      ))}
    </SidebarMenu>
  );
};

// A sidenav you drill into (settings, a tool, an inbox, a workspace): a back
// arrow and title, an optional list on top, then its sections, in groups.
export const SectionSideNav = ({
  title,
  onBack,
  isBackLoading,
  testIdPrefix,
  groups,
  top,
}: {
  title: ReactNode;
  onBack: () => void;
  isBackLoading?: boolean;
  testIdPrefix: string;
  groups: ISectionNavGroup[];
  // Listed above the sections, with a line between (a tool's open tools).
  top?: ReactNode;
}) => {
  const shown = groups.filter((group) => group.items.length > 0);
  // A lone section is not worth listing (a tool with one).
  const listed =
    shown.length === 1 && !shown[0].label && shown[0].items.length < 2
      ? []
      : shown;

  return (
    <SideNavShell
      header={
        <BackTitle
          title={title}
          onBack={onBack}
          isBackLoading={isBackLoading}
          testIdPrefix={testIdPrefix}
        />
      }
      testIdPrefix={testIdPrefix}
      // Below a list on top, the sections follow a line with 12px either
      // side. Room at the end, so a long list scrolls clear of the footer.
      contentClassName={cn("pb-4", top && "gap-0 pt-0")}
    >
      {top}
      {listed.map((group, index) => (
        <SidebarGroup
          key={group.label ?? index}
          className={cn(
            "px-2",
            top ? (index === 0 ? "pt-3 pb-0" : "pt-2 pb-0") : "py-0"
          )}
        >
          {top && index === 0 && (
            <div className="pb-3">
              <div className="border-b border-sidebar-border" />
            </div>
          )}
          {group.label && <SidebarGroupLabel>{group.label}</SidebarGroupLabel>}
          <SidebarGroupContent>
            <SectionNavList items={group.items} />
          </SidebarGroupContent>
        </SidebarGroup>
      ))}
    </SideNavShell>
  );
};

const SectionNavEntry = ({ item }: { item: ISectionNavItem }) => {
  if ("children" in item) {
    return (
      <SideNavExpandableEntry
        title={item.title}
        icon={item.icon}
        hasActiveChild={item.children.some((page) => page.isActive)}
        testId={`section-nav-${item.id}`}
      >
        {item.children.map(({ id, ...page }) => (
          <SideNavSubEntry key={id} testId={`section-nav-${id}`} {...page} />
        ))}
      </SideNavExpandableEntry>
    );
  }
  if ("href" in item) {
    return (
      <SidebarMenuItem className="list-none">
        <SidebarMenuButton
          asChild
          isActive={item.isActive}
          className="group/menu-button"
          data-testid={`section-nav-${item.id}`}
          data-tutorial={`tab-${item.id}`}
        >
          <Link href={item.href} className="flex items-center w-full">
            <EntryIcon icon={item.icon} />
            <EntryLabel>{item.title}</EntryLabel>
          </Link>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  }
  return (
    <SidebarMenuItem className="list-none" role="presentation">
      <SidebarMenuButton
        role="tab"
        tabIndex={item.isActive ? 0 : -1}
        aria-selected={item.isActive}
        data-state={item.isActive ? "active" : "inactive"}
        isActive={item.isActive}
        disabled={item.isDisabled}
        onClick={item.onSelect}
        className="group/menu-button"
        data-testid={`section-nav-${item.id}`}
        data-tutorial={`tab-${item.id}`}
      >
        <EntryIcon icon={item.icon} />
        <EntryLabel>{item.title}</EntryLabel>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
};
